/**
 * Theme Toggle Utility
 * 
 * Provides support for manual theme toggling while defaulting to
 * the user's OS/browser preference via prefers-color-scheme.
 * 
 * Usage:
 *   - ThemeManager.setTheme('dark' | 'light' | 'system')
 *   - ThemeManager.getTheme() -> 'dark' | 'light' | 'system'
 *   - ThemeManager.getEffectiveTheme() -> 'dark' | 'light'
 */

class ThemeManager {
  static STORAGE_KEY = 'theme-preference';
  static THEME_ATTRIBUTE = 'data-theme';

  /**
   * Initialize theme manager on page load
   */
  static init() {
    // Apply saved preference or default to system preference
    const savedTheme = this.getTheme();
    if (savedTheme && savedTheme !== 'system') {
      this.applyTheme(savedTheme);
    } else {
      // Default to system preference
      this.applySystemTheme();
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      // Only respond to system changes if user hasn't set a manual preference
      if (!this.getTheme() || this.getTheme() === 'system') {
        this.applySystemTheme();
      }
    });
  }

  /**
   * Get the current theme preference from localStorage
   * @returns {string|null} 'dark', 'light', 'system', or null
   */
  static getTheme() {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (e) {
      return null;
    }
  }

  /**
   * Get the effective theme (resolves 'system' to actual theme)
   * @returns {string} 'dark' or 'light'
   */
  static getEffectiveTheme() {
    const preference = this.getTheme();
    if (preference === 'dark' || preference === 'light') {
      return preference;
    }
    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  /**
   * Set theme preference
   * @param {string} theme - 'dark', 'light', or 'system'
   */
  static setTheme(theme) {
    if (!['dark', 'light', 'system'].includes(theme)) {
      console.warn(`Invalid theme: ${theme}. Must be 'dark', 'light', or 'system'.`);
      return;
    }

    try {
      if (theme === 'system') {
        localStorage.removeItem(this.STORAGE_KEY);
      } else {
        localStorage.setItem(this.STORAGE_KEY, theme);
      }

      if (theme === 'system') {
        this.applySystemTheme();
      } else {
        this.applyTheme(theme);
      }
    } catch (e) {
      console.warn('Failed to save theme preference:', e);
    }
  }

  /**
   * Apply a specific theme
   * @param {string} theme - 'dark' or 'light'
   */
  static applyTheme(theme) {
    if (theme === 'dark') {
      document.documentElement.setAttribute(this.THEME_ATTRIBUTE, 'dark');
    } else {
      document.documentElement.setAttribute(this.THEME_ATTRIBUTE, 'light');
    }
  }

  /**
   * Apply theme based on system preference
   */
  static applySystemTheme() {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    // Remove attribute to let CSS prefers-color-scheme handle it
    document.documentElement.removeAttribute(this.THEME_ATTRIBUTE);
  }

  /**
   * Toggle between dark and light (ignores system preference)
   */
  static toggle() {
    const current = this.getEffectiveTheme();
    const newTheme = current === 'dark' ? 'light' : 'dark';
    this.setTheme(newTheme);
    return newTheme;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => ThemeManager.init());
} else {
  ThemeManager.init();
}

// Export for use in other scripts
window.ThemeManager = ThemeManager;

